<template>
  <section class="position-relative" :class="containerClass">
    <!-- content container -->
    <div class="container content-container position-relative">
      <div
        class="pt-space-xs pt-md-space-md pb-space-md px-0 px-lg-space-lg content-div">
        <div class="row">
          <div class="col-12 col-md-10 mx-auto">
            <div class="px-space-xs px-md-space-xxs">
              <div
                class="d-none d-md-block fs-xs mb-space-md"
                v-if="showBreadcrumbs">
                <template v-for="(item, index) in breadcrumbs">
                  <label v-if="isLastItem(index, breadcrumbs)" :key="index">
                    <a
                      :href="item.link"
                      :target="item.target"
                      class="text-primary"
                      @click="handleBreadcrumbLinkClickEvent($event, item)">
                      {{ item.text }}
                    </a>
                    <span class="mx-space-xxs">/</span>
                  </label>
                  <label v-else :key="index + '-haa'">
                    {{ item.text }}
                  </label>
                </template>
              </div>
              <component
                :is="titleLevel"
                :class="titleClass"
                class="mb-space-sm">
                {{ title }}
              </component>

              <div class="mb-space-lg" :class="articleInfoClass">
                {{ articleDate }}
                <span class="fw-bold mx-space-xxs">·</span>
                {{ minutesRead }} min read
                <span class="fw-bold mx-space-xxs">·</span>
                By {{ postedBy }}
              </div>
            </div>
          </div>
          <div class="col-12 px-0 px-md-gutter">
            <!-- image container -->
            <div class="container-md px-0 px-md-gutter">
              <div class="row haa-header mx-0 mx-md-space-md mb-space-md">
                <slot name="headerBg">
                  <div
                    class="col-12 haa-header-image px-0"
                    :style="bgImage"></div>
                </slot>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-10 mx-auto">
            <div class="px-space-xs px-md-space-xxs pb-space-md">
              <slot name="body"></slot>
              <div
                class="d-flex flex-row flex-nowrap justify-content-start align-items-center"
                v-if="showSocialMedia">
                <span class="fs-large fw-bold me-space-xs">
                  {{ socialMediaText }}
                </span>
                <a
                  v-for="(item, index) in socialMediaItems"
                  :key="index"
                  :class="linkClass(index, socialMediaItems)"
                  :href="item.link"
                  :title="item.ariaText"
                  :aria-label="item.ariaText"
                  target="_blank"
                  @click="handleExternalLinkClickEvent($event, item.ariaText)">
                  <font-awesome-icon
                    role="presentation"
                    :icon="getIcon(item.icon)"
                    :alt="item.ariaText"
                    size="1x"
                    class="text-dark-3 social-media-icon"></font-awesome-icon>
                </a>
              </div>
              <hr class="d-none d-md-block mt-space-lg mb-0 border-light-4" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import {
  faFacebookSquare,
  faInstagramSquare,
  faLinkedin,
  faTwitterSquare,
  faYoutubeSquare,
} from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { computed } from "vue";

export interface BreadCrumb {
  link?: string;
  target?: string;
  text: string;
}

export interface SocialMediaItem {
  icon: keyof typeof socialIconMap;
  link: string;
  ariaText: string;
}

export interface Props {
  articleInfoSize?: "small" | "medium" | "large" | "xl";
  articleDate: string;
  minutesRead: number;
  postedBy: string;
  showSocialMedia?: boolean;
  socialMediaItems?: SocialMediaItem[];
  socialMediaText?: string;
  showBreadcrumbs?: boolean;
  breadcrumbs?: BreadCrumb[];
  bgImageSource?: string;
  containerBgVariant?: string;
  title: string;
  titleLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  titleSize?: "small" | "medium" | "large" | "xl";
  titleVariant?: string;
}

const props = withDefaults(defineProps<Props>(), {
  articleInfoSize: "small",
  showSocialMedia: true,
  socialMediaItems: () => [],
  socialMediaText: "Share :",
  showBreadcrumbs: true,
  breadcrumbs: () => [],
  containerBgVariant: "white",
  titleLevel: "h1",
  titleSize: "medium",
  titleVariant: "dark-3",
});

// events
const emits = defineEmits<{
  /**
   *  Emitted when any part of the card is clicked except for cta click.
   */
  (e: "onSocialMediaClick", event: Event, text: string): void;
  /**
   *  Emitted when one of the bread crumb links is clicked.
   */
  (e: "onBreadcrumbClick", event: Event, item: BreadCrumb): void;
}>();

const socialIconMap = {
  facebook: faFacebookSquare,
  instagram: faInstagramSquare,
  linkedin: faLinkedin,
  twitter: faTwitterSquare,
  youtube: faYoutubeSquare,
};

const getIcon = computed(() => {
  return (icon: keyof typeof socialIconMap) => socialIconMap[icon];
});

const linkClass = computed(() => {
  return (index: number, items: SocialMediaItem[]) =>
    index !== items.length - 1 ? "me-space-xs" : "";
});

const isLastItem = computed(() => {
  return (index: number, items: BreadCrumb[]) => index !== items.length - 1;
});

const articleInfoClass = computed(() => {
  return `fs-${props.articleInfoSize}`;
});

const bgImage = computed(() => {
  return { "background-image": `url('${props.bgImageSource}')` };
});

const containerClass = computed(() => {
  return `bg-${props.containerBgVariant}`;
});

const titleClass = computed(() => {
  return `${props.titleLevel}-${props.titleSize} text-${props.titleVariant}`;
});

const handleExternalLinkClickEvent = (event: Event, text: string) => {
  emits("onSocialMediaClick", event, text);
};

const handleBreadcrumbLinkClickEvent = (event: Event, item: BreadCrumb) => {
  emits("onBreadcrumbClick", event, item);
};
</script>

<style lang="scss" scoped>
.social-media-icon {
  height: 32px !important;
  width: 32px !important;
}

.haa-header {
  height: 256px;
}
.haa-header-image {
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top;
}

@media (min-width: 768px) {
  .haa-header {
    height: 600px;
  }
  .content-container {
    max-width: 1140px;
  }
}
</style>
